import Project, { ProjectMilestone } from 'models/projects/project';
import { ProjectState } from 'models/projects/projectEnums';
import { parsePositiveIntegerRouteParam } from 'utils/url';

/**
 * Store deep link at `/admin/store/content` (not `/admin/store`, listed in `globalReturnUrls`).
 * Optional `?package=` takes a package id.
 */
export const getProjectStoreContentRoute = (packageId?: number): string => {
  if (packageId === undefined) return '/admin/store/content';

  return `/admin/store/content?package=${packageId}`;
};

/** Builds `/project/{id}`. */
export const getProjectRoute = (projectId: number): string => {
  return `/project/${projectId}`;
};

/** Builds `/project/{id}/milestone/{taskId}` by platform task id. */
export const getProjectMilestoneRoute = (projectId: number, milestoneTaskId: number): string => {
  return `${getProjectRoute(projectId)}/milestone/${milestoneTaskId}`;
};

/**
 * Milestone that completed between two reads of the same project, if any. Undefined on project switch
 * or when the project is archived.
 */
export const getNewlyCompletedMilestone = (
  previous: Project | undefined,
  next: Project,
): ProjectMilestone | undefined => {
  if (previous === undefined || previous.projectId !== next.projectId) return undefined;
  if (next.state === ProjectState.archived) return undefined;

  return next.milestones.find(
    (m) => m.completed && previous.milestones.some((p) => p.taskId === m.taskId && !p.completed),
  );
};

/**
 * Builds `/project/{id}/step/{taskId}`. Omits milestone id; `ProjectTask.MilestoneTaskId` already
 * identifies the milestone.
 */
export const getProjectStepRoute = (projectId: number, taskId: number): string => {
  return `${getProjectRoute(projectId)}/step/${taskId}`;
};

/** Builds `/project/{id}/room/{roomKey}` by registry room key. */
export const getProjectRoomRoute = (projectId: number, roomKey: string): string => {
  return `${getProjectRoute(projectId)}/room/${encodeURIComponent(roomKey)}`;
};

/** Parses a milestone or step route segment as a task id. Format only; membership is checked after load. */
export const parseProjectRouteTaskId = (segment: string | undefined): number | undefined => {
  return parsePositiveIntegerRouteParam(segment);
};

export const getProjectRoomItemRoute = (projectId: number, roomKey: string, itemId: string): string =>
  `${getProjectRoomRoute(projectId, roomKey)}/${encodeURIComponent(itemId)}`;
